import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Edit, Trash2, X, Accessibility, Star, Heart, Crown, Armchair } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaSeatType } from '@/types';

const seatTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  priceModifier: z.number(),
  color: z.string().min(1, 'Color is required'),
  icon: z.string().optional(),
  widthMultiplier: z.number().min(1).max(3),
  seatStyle: z.enum(['single', 'double', 'loveseat', 'table']),
  sortOrder: z.number(),
});

type SeatTypeFormData = z.infer<typeof seatTypeSchema>;

const DEFAULT_COLORS = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#06B6D4', // Cyan
  '#6B7280', // Gray
];

const AVAILABLE_ICONS = [
  { value: '', label: 'None', icon: null },
  { value: 'wheelchair', label: 'Wheelchair', icon: <Accessibility className="h-4 w-4" /> },
  { value: 'star', label: 'Star', icon: <Star className="h-4 w-4" /> },
  { value: 'heart', label: 'Heart', icon: <Heart className="h-4 w-4" /> },
  { value: 'vip', label: 'VIP', icon: <Crown className="h-4 w-4" /> },
  { value: 'premium', label: 'Premium', icon: <Armchair className="h-4 w-4" /> },
];

const SEAT_STYLES = [
  { value: 'single', label: 'Single Seat', width: 1 },
  { value: 'double', label: 'Double Seat', width: 2 },
  { value: 'loveseat', label: 'Loveseat', width: 1.5 },
  { value: 'table', label: 'Table Seat', width: 1 },
];

export default function SeatTypesList() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<CinemaSeatType | null>(null);

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: seatTypes, isLoading } = useQuery<CinemaSeatType[]>({
    queryKey: ['cinema-seat-types', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/seat-types`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SeatTypeFormData>({
    resolver: zodResolver(seatTypeSchema),
    defaultValues: {
      name: '',
      description: '',
      priceModifier: 0,
      color: '#3B82F6',
      icon: '',
      widthMultiplier: 1,
      seatStyle: 'single',
      sortOrder: 0,
    },
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');
  const selectedStyle = watch('seatStyle');

  const createMutation = useMutation({
    mutationFn: async (data: SeatTypeFormData) => {
      await api.post(`/admin/venues/${venueId}/cinema/seat-types`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-seat-types', venueId] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: SeatTypeFormData) => {
      await api.put(`/admin/venues/${venueId}/cinema/seat-types/${editingType?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-seat-types', venueId] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (typeId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/seat-types/${typeId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-seat-types', venueId] });
    },
  });

  const openModal = (seatType?: CinemaSeatType) => {
    if (seatType) {
      setEditingType(seatType);
      reset({
        name: seatType.name,
        description: seatType.description || '',
        priceModifier: seatType.price_modifier,
        color: seatType.color,
        icon: seatType.icon || '',
        widthMultiplier: seatType.width_multiplier || 1,
        seatStyle: seatType.seat_style || 'single',
        sortOrder: seatType.sort_order,
      });
    } else {
      setEditingType(null);
      reset({
        name: '',
        description: '',
        priceModifier: 0,
        color: '#3B82F6',
        icon: '',
        widthMultiplier: 1,
        seatStyle: 'single',
        sortOrder: (seatTypes?.length || 0) + 1,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    reset();
  };

  const onSubmit = (data: SeatTypeFormData) => {
    if (editingType) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const getIconDisplay = (iconValue?: string) => {
    const iconConfig = AVAILABLE_ICONS.find(i => i.value === iconValue);
    return iconConfig?.icon || null;
  };

  const getStyleLabel = (style?: string) => {
    const styleConfig = SEAT_STYLES.find(s => s.value === style);
    return styleConfig?.label || 'Single';
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Seat Types</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - Configure seat categories
          </p>
        </div>
        <button onClick={() => openModal()} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Seat Type
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Style
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price Modifier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-8 w-8 bg-gray-200 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  </tr>
                ))
              ) : seatTypes?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No seat types configured. Add seat types like "Premium", "Standard", "Accessible".
                  </td>
                </tr>
              ) : (
                seatTypes?.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div
                        className="h-8 rounded-md border-2 border-gray-200 flex items-center justify-center text-white text-xs font-medium"
                        style={{
                          backgroundColor: type.color,
                          width: 32 * (type.width_multiplier || 1)
                        }}
                      >
                        {getIconDisplay(type.icon)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{type.name}</p>
                        {type.description && (
                          <p className="text-sm text-gray-500">{type.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {getStyleLabel(type.seat_style)}
                      {type.width_multiplier && type.width_multiplier !== 1 && (
                        <span className="text-gray-400 ml-1">({type.width_multiplier}x)</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {type.price_modifier > 0 && (
                        <span className="text-green-600 font-medium">
                          +{type.price_modifier.toFixed(2)}
                        </span>
                      )}
                      {type.price_modifier < 0 && (
                        <span className="text-red-600 font-medium">
                          {type.price_modifier.toFixed(2)}
                        </span>
                      )}
                      {type.price_modifier === 0 && (
                        <span className="text-gray-500">Base price</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        type.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openModal(type)}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this seat type?')) {
                              deleteMutation.mutate(type.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editingType ? 'Edit Seat Type' : 'Add Seat Type'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="input"
                  placeholder="e.g., Premium, Standard"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  {...register('description')}
                  className="input"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seat Style
                </label>
                <select
                  {...register('seatStyle')}
                  className="input"
                  onChange={(e) => {
                    const style = SEAT_STYLES.find(s => s.value === e.target.value);
                    if (style) {
                      setValue('widthMultiplier', style.width);
                    }
                    setValue('seatStyle', e.target.value as 'single' | 'double' | 'loveseat' | 'table');
                  }}
                >
                  {SEAT_STYLES.map((style) => (
                    <option key={style.value} value={style.value}>
                      {style.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Width Multiplier
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="3"
                  {...register('widthMultiplier', { valueAsNumber: true })}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  1 = normal width, 2 = double width
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon (optional)
                </label>
                <div className="flex gap-2">
                  {AVAILABLE_ICONS.map((iconOption) => (
                    <button
                      key={iconOption.value}
                      type="button"
                      onClick={() => setValue('icon', iconOption.value)}
                      className={`p-2 rounded-md border-2 flex items-center gap-1 ${
                        selectedIcon === iconOption.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      title={iconOption.label}
                    >
                      {iconOption.icon || <span className="text-xs text-gray-400">None</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Modifier
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    £
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    {...register('priceModifier', { valueAsNumber: true })}
                    className="input pl-7"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Add (+) or subtract (-) from base price
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {DEFAULT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setValue('color', color)}
                      className={`h-8 w-8 rounded-md border-2 ${
                        selectedColor === color ? 'border-gray-900' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  {...register('color')}
                  className="h-10 w-full cursor-pointer"
                />
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preview
                </label>
                <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-lg">
                  <div
                    className="h-10 rounded-md flex items-center justify-center text-white text-sm font-medium"
                    style={{
                      backgroundColor: selectedColor,
                      width: 40 * (watch('widthMultiplier') || 1)
                    }}
                  >
                    {getIconDisplay(selectedIcon) || '1'}
                  </div>
                  <span className="text-sm text-gray-600">
                    {watch('name') || 'Seat Type'} - {getStyleLabel(selectedStyle)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className="input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingType
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
